import React from "react";
import {
	Box,
	Button
} from "@mui/material";
import {
	makeStyles
} from "@mui/styles";
import {
	Sms as SmsIcon
} from "@mui/icons-material";
import {
	VisibleContent
} from "../../../../../components";
import {getImage} from "../../../../../utils/image";
import {addToClipboard} from "../../../../../utils/clipboard";
import {notification, notificationTypes} from "../../../../../utils/notification";
import clsx from "clsx";
import {useBuyNumber} from "../../../../../contexts/BuyNumberContext";
import getTrans from "../../../../../languages/getTrans";

const NumberCard = ({ item, onCancel, onRepeat }) => {
	const classes = useStyles();
	const buyNumber = useBuyNumber();

	const [phoneMask] = React.useState(() => {
		return `+${item.country_phone_code} ${String(item.number || '').replace(item.country_phone_code, '')}`
	});

	const [countdown, setCountdown] = React.useState(0);
	const timerIntervalRef = React.useRef(null);

	React.useEffect(() => {
		if (timerIntervalRef.current) {
			clearInterval(timerIntervalRef.current);
			timerIntervalRef.current = null;
		}

		if (!item.timestamp_end) {
			setCountdown(0);
			return;
		}

		const updateCountdown = () => {
			const endTime = new Date(item.timestamp_end * 1000).valueOf();
			const now = Date.now();
			const difference = endTime - now;

			if (difference <= 0) {
				setCountdown(0);
				if (timerIntervalRef.current) {
					clearInterval(timerIntervalRef.current);
					timerIntervalRef.current = null;
				}
				return;
			}

			setCountdown(difference);
		};

		updateCountdown();

		const endTime = new Date(item.timestamp_end * 1000).valueOf();
		const now = Date.now();
		const initialDifference = endTime - now;

		if (initialDifference > 0) {
			timerIntervalRef.current = setInterval(updateCountdown, 1000);
		} else {
			setCountdown(0);
		}

		return () => {
			if (timerIntervalRef.current) {
				clearInterval(timerIntervalRef.current);
				timerIntervalRef.current = null;
			}
		};
	}, [item.timestamp_end]);

	const [cancelCountdown, setCancelCountdown] = React.useState(0);
	const cancelTimerIntervalRef = React.useRef(null);

	React.useEffect(() => {
		if (cancelTimerIntervalRef.current) {
			clearInterval(cancelTimerIntervalRef.current);
			cancelTimerIntervalRef.current = null;
		}

		if (!item.timestamp) {
			setCancelCountdown(0);
			return;
		}

		const allowCancelSec = item.allow_cancel_sec || 90;
		const cancelEndTime = item.timestamp * 1000 + allowCancelSec * 1000;

		const updateCancelCountdown = () => {
			const now = Date.now();
			const difference = cancelEndTime - now;

			if (difference <= 0) {
				setCancelCountdown(0);
				if (cancelTimerIntervalRef.current) {
					clearInterval(cancelTimerIntervalRef.current);
					cancelTimerIntervalRef.current = null;
				}
				return;
			}

			setCancelCountdown(difference);
		};

		updateCancelCountdown();

		const now = Date.now();
		const initialDifference = cancelEndTime - now;

		if (initialDifference > 0) {
			cancelTimerIntervalRef.current = setInterval(updateCancelCountdown, 1000);
		} else {
			setCancelCountdown(0);
		}

		return () => {
			if (cancelTimerIntervalRef.current) {
				clearInterval(cancelTimerIntervalRef.current);
				cancelTimerIntervalRef.current = null;
			}
		};
	}, [item.timestamp, item.allow_cancel_sec]);

	const code = item.code;
	const isActiveNumber = item.status === 1;
	const multiple_codes_length = (item.multiple_codes || []).length;
	const isWaitSms = React.useMemo(() => {
		if (!isActiveNumber || countdown <= 0) return false;
		return (!code && !multiple_codes_length) || multiple_codes_length > 0;
	}, [isActiveNumber, code, multiple_codes_length, countdown]);
	const canCancel = cancelCountdown <= 0;
	const codes = (Boolean(multiple_codes_length > 0) ? item.multiple_codes : [code]).filter((t) => !!t);
	const formatTime = (ms) => {
		const seconds = Math.floor(ms / 1000);
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${String(secs).padStart(2, '0')}`;
	};

	const handleCopyNumber = () => {
		addToClipboard(item.number);
		notification(({
			message: getTrans('numbersReceived.Copied!'),
			type: notificationTypes.success
		}))
	}
	const handleCopyCode = (code) => {
		addToClipboard(code);
		notification(({
			message: getTrans('numbersReceived.Copied!'),
			type: notificationTypes.success
		}))
	}

	return (
		<div className={classes.card}>
			<div className={classes.cardRowInfo}>
				<div className={classes.cardRowInfoIcon}>
					<img src={getImage(item.service_icon)}/>
				</div>
				<div className={classes.cardRowInfoName}>{item.service_name}</div>
			</div>
			<Button className={classes.cardRowInfo} onClick={handleCopyNumber}>
				<div className={classes.cardRowInfoIcon}>
					<img src={getImage(item.country_icon)}/>
				</div>
				<div className={classes.cardRowInfoName}>{phoneMask}</div>
				<div className={classes.cardRowInfoCopy}>
					<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M23.625 3.5H9.625C9.39294 3.5 9.17038 3.59219 9.00628 3.75628C8.84219 3.92038 8.75 4.14294 8.75 4.375V8.75H4.375C4.14294 8.75 3.92038 8.84219 3.75628 9.00628C3.59219 9.17038 3.5 9.39294 3.5 9.625V23.625C3.5 23.8571 3.59219 24.0796 3.75628 24.2437C3.92038 24.4078 4.14294 24.5 4.375 24.5H18.375C18.6071 24.5 18.8296 24.4078 18.9937 24.2437C19.1578 24.0796 19.25 23.8571 19.25 23.625V19.25H23.625C23.8571 19.25 24.0796 19.1578 24.2437 18.9937C24.4078 18.8296 24.5 18.6071 24.5 18.375V4.375C24.5 4.14294 24.4078 3.92038 24.2437 3.75628C24.0796 3.59219 23.8571 3.5 23.625 3.5ZM22.75 17.5H19.25V9.625C19.25 9.39294 19.1578 9.17038 18.9937 9.00628C18.8296 8.84219 18.6071 8.75 18.375 8.75H10.5V5.25H22.75V17.5Z" fill="#FB7800"/>
					</svg>
				</div>
			</Button>
			<VisibleContent visible={codes.length > 0}>
				{codes.map((code, index) => (
					<Button key={`code-${index}-${code}`} className={classes.cardRowInfo} onClick={() => handleCopyCode(code)}>
						<Box className={classes.cardRowInfoIcon} sx={{background: "#236A37"}}>
							<SmsIcon sx={{color: "white", fontSize: 16}}/>
						</Box>
						<div className={classes.cardRowInfoName}>{code}</div>
						<div className={classes.cardRowInfoCopy}>
							<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M23.625 3.5H9.625C9.39294 3.5 9.17038 3.59219 9.00628 3.75628C8.84219 3.92038 8.75 4.14294 8.75 4.375V8.75H4.375C4.14294 8.75 3.92038 8.84219 3.75628 9.00628C3.59219 9.17038 3.5 9.39294 3.5 9.625V23.625C3.5 23.8571 3.59219 24.0796 3.75628 24.2437C3.92038 24.4078 4.14294 24.5 4.375 24.5H18.375C18.6071 24.5 18.8296 24.4078 18.9937 24.2437C19.1578 24.0796 19.25 23.8571 19.25 23.625V19.25H23.625C23.8571 19.25 24.0796 19.1578 24.2437 18.9937C24.4078 18.8296 24.5 18.6071 24.5 18.375V4.375C24.5 4.14294 24.4078 3.92038 24.2437 3.75628C24.0796 3.59219 23.8571 3.5 23.625 3.5ZM22.75 17.5H19.25V9.625C19.25 9.39294 19.1578 9.17038 18.9937 9.00628C18.8296 8.84219 18.6071 8.75 18.375 8.75H10.5V5.25H22.75V17.5Z" fill="#FB7800"/>
							</svg>
						</div>
					</Button>
				))}
			</VisibleContent>
			<VisibleContent visible={isWaitSms}>
				<WaitSmsBlock countdown={countdown} timestamp={item.timestamp} timestampEnd={item.timestamp_end} classes={classes} />
				<Button
					variant="contained" fullWidth
					className={classes.cardGetNewNumber} disabled={!canCancel}
					onClick={onCancel}
				>
					{canCancel ? getTrans('numbersReceived.Отменить') : `${getTrans('my_numbers.Доступно через')} ${formatTime(cancelCountdown)}`}
				</Button>
			</VisibleContent>
			<Button variant="contained" fullWidth className={classes.cardGetNewNumber} onClick={onRepeat}>
				{getTrans('numbersReceived.Получить новый номер')}
			</Button>
		</div>
	)
}
const WaitSmsBlock = ({ countdown, timestamp, timestampEnd, classes }) => {
	const formatTime = (ms) => {
		const seconds = Math.floor(ms / 1000);
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${String(secs).padStart(2, '0')}`;
	};
	const totalTime = timestamp && timestampEnd ? (timestampEnd - timestamp) * 1000 : 1200000;
	const percent = Math.max(0, Math.min(100, countdown > 0 ? (1 - (countdown / totalTime)) * 100 : 0));

	if (countdown <= 0) {
		return null;
	}
	return (
		<div className={clsx([classes.cardRowInfo, classes.waitSmsBlock])}>
			<div className={classes.waitSmsText}>
				{getTrans('my_numbers.Ожидаем СМС')} {formatTime(countdown)}
			</div>
			<div className={classes.waitSmsProgress} style={{ width: `${percent}%` }}/>
		</div>
	);
};

const useStyles = makeStyles(theme => ({
	card: {
		gap: "2px",
		padding: "8px",
		display: "flex",
		borderRadius: "12px",
		flexDirection: "column",
		boxSizing: "border-box",
		backgroundColor: theme.palette.header.default,
	},

	cardRowInfo: {
		gap: "8px",
		padding: "6px",
		display: "flex",
		borderRadius: "8px",
		alignItems: "center",
		boxSizing: "border-box",
		background: "#24323A!important",
	},
	cardRowInfoIcon: {
		width: 32,
		height: 32,
		minWidth: 32,
		display: "flex",
		overflow: "hidden",
		alignItems: "center",
		position: "relative",
		borderRadius: "100%",
		justifyContent: "center",

		"& img": {
			width: "100%",
			height: "100%",
			objectFit: "cover",
			objectPosition: "50% 50%",
		}
	},
	cardRowInfoName: {
		flex: 1,
		display: "flex",
		fontSize: "18px",
		color: "#E7E6E6",
		fontWeight: "500",
		lineHeight: "22px",
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	cardRowInfoCopy: {
		width: 32,
		height: 32,
		minWidth: 32,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",

	},

	cardGetNewNumber: {
		height: 40,
		fontSize: 15,
		fontWeight: "600",
		lineHeight: "18px",
		letterSpacing: "0.02em",
	},

	waitSmsBlock: {
		zIndex: 0,
		height: 44,
		overflow: "hidden",
		position: "relative",
	},
	waitSmsIcon: {
		width: 32,
		height: 32,
		minWidth: 32,
		borderRadius: "8px",
		background: "#5D4037",
	},
	waitSmsText: {
		flex: 1,
		fontSize: "16px",
		color: "#E7E6E6",
		fontWeight: "500",
		lineHeight: "20px",
		textAlign: "center",
	},
	waitSmsProgress: {
		position: "absolute",
		zIndex: -1,
		opacity: 0.2,
		height: "100%",
		top: 0, left: 0,
		background: "#FB7800",
		pointerEvents: "none",
	}
}));

export default NumberCard;
